import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useContext, useState, useEffect } from 'react';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import Tooltip from '@mui/material/Tooltip';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

dayjs.extend(isBetween);

import { ClinicRxContext } from '../../App';
import { getServerURL, downloadData } from '../../utils';
import ChipsAndCharts from './ChipsAndCharts';
import {
  SET_CHART_DATA,
  SET_CHART_DATA_FETCHED,
} from '../../dispatchActionTypes';

const ALL_DATA = 'All Data';
const DATE_RANGE = 'Date Range';
const ALL_CHIPS = [ALL_DATA, DATE_RANGE];
const SERVER_URL = getServerURL();

const threeMonthsAgo = dayjs().subtract(3, 'month');

export default function ChartPage() {
  const { state, dispatch, fetchProtected } = useContext(ClinicRxContext);
  const [dataOption, setDataOption] = useState(ALL_DATA);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fromDate, setFromDate] = useState(threeMonthsAgo);
  const [toDate, setToDate] = useState(dayjs());
  const [snackbar, setSnackbar] = useState({ isOpen: false, message: '' });

  const filterChartData = () => {
    // There can already be some just
    // completed alerts on the dashboard
    const completedAlreadyInState = new Set();
    for (const { alertStatus, alertID } of state.alertData) {
      if (alertStatus === 3) {
        completedAlreadyInState.add(alertID);
      }
    }
    // Don't add the same completed alert twice.
    const potentialDupsRemoved = state.chartData.content.filter(
      ({ alertID }) => !completedAlreadyInState.has(alertID),
    );

    const sanitizedChartData = [...state.alertData, ...potentialDupsRemoved];

    if (dataOption === ALL_DATA) return sanitizedChartData;
    return sanitizedChartData.filter((alertElement) => {
      const dAlertDate = dayjs(alertElement.alertDate);
      return dAlertDate.isBetween(fromDate, toDate, 'day', '[]');
      // Cache date range filter results on chartData?
    });
  };

  const filteredChartData = filterChartData();

  useEffect(() => {
    // Get the completed alerts from the database.
    // Alerts completed in real-time and in complete alerts will be on the
    // dashboard already.
    const controller = new AbortController();
    const fetchChartData = async () => {
      try {
        const response = await fetchProtected((bearerToken) =>
          fetch(
            `${SERVER_URL}/get-chart-data?userType=${state.user.userType}&organization=${encodeURIComponent(state.user.organization)}`,
            {
              signal: controller.signal,
              headers: {
                authorization: bearerToken,
              },
            },
          ),
        );
        if (response.ok) {
          const json = await response.json();
          const { chartAlerts } = json;
          dispatch({
            type: SET_CHART_DATA,
            payload: { chartData: chartAlerts },
          });
        } else {
          let message = 'Error retrieving completed alerts.';
          setSnackbar({
            isOpen: true,
            message,
          });
          dispatch({ type: SET_CHART_DATA_FETCHED });
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        console.error('Error making fetch request to GET chart data', err);
      }
    };
    if (!state.chartData.isFetched) {
      fetchChartData();
    }
    return () => {
      controller.abort();
    };
  }, []);

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <Box
        sx={{
          padding: '20px 0px',
          gap: '15px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: '6vw' }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: '5px' }}>
            {ALL_CHIPS.map((option) => {
              return (
                <Chip
                  key={option}
                  label={
                    option === dataOption && state.chartData.isFetched
                      ? `${option}: ${filteredChartData.length}`
                      : option
                  }
                  color={option === dataOption ? 'primary' : 'default'}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => {
                    if (option === ALL_DATA) {
                      setDataOption(option);
                    } else {
                      setIsModalOpen(true);
                    }
                  }}
                />
              );
            })}
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {state.chartData.isFetched && (
              <Tooltip title='Download'>
                <DownloadForOfflineIcon
                  color='primary'
                  sx={{ cursor: 'pointer' }}
                  onClick={() => downloadData(filteredChartData)}
                />
              </Tooltip>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex' }}>
          {!state.chartData.isFetched ? (
            <CircularProgress aria-label='Loading…' />
          ) : !filteredChartData.length ? (
            <Typography>No data to display</Typography>
          ) : (
            <ChipsAndCharts
              chartData={filteredChartData}
              userType={state.user.userType}
            />
          )}
        </Box>
      </Box>
      <Dialog
        open={isModalOpen}
        onClose={handleModalClose}
      >
        <DialogTitle>Select Date Range</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ padding: '10px' }}>
              <DatePicker
                label='From'
                value={fromDate}
                onChange={(newValue) => {
                  setFromDate(newValue);
                }}
                disableFuture
              />
            </Box>
            <Box sx={{ padding: '10px' }}>
              <DatePicker
                label='To'
                value={toDate}
                onChange={(newValue) => {
                  setToDate(newValue);
                }}
                disableFuture
              />
            </Box>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button
            variant='outlined'
            onClick={() => {
              if (dataOption === ALL_DATA) {
                setDataOption(DATE_RANGE);
              }
              // Re-renders are automatically triggered by updating
              // date pickers
              handleModalClose();
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={snackbar.isOpen}
        onClose={() => {
          setSnackbar({
            isOpen: false,
            message: '',
          });
        }}
        autoHideDuration={12000}
        key={'topcenter'}
      >
        <Alert severity='warning'>{snackbar.message}</Alert>
      </Snackbar>
    </>
  );
}
